import { supabase } from '../client';
import { EmailThread } from '@core/types';

export const emailService = {
  async listByOrg(orgId: string): Promise<EmailThread[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_email_threads')
      .select(`
        *,
        application:ggj_b2b_applications(
          id, status,
          candidate:ggj_b2b_candidates(id, full_name),
          job:ggj_b2b_jobs(id, title),
          client:ggj_b2b_clients(id, name)
        )
      `)
      .eq('org_id', orgId)
      .order('sent_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as EmailThread[];
  },

  async listByApplication(applicationId: string | null | undefined): Promise<EmailThread[]> {
    if (!applicationId || applicationId === 'undefined') return [];
    const { data, error } = await supabase
      .from('ggj_b2b_email_threads')
      .select('*')
      .eq('application_id', applicationId)
      .order('sent_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as EmailThread[];
  },

  /** List email threads for applications belonging to a client */
  async listByClient(orgId: string, clientId: string): Promise<EmailThread[]> {
    const { data: appIds } = await supabase
      .from('ggj_b2b_applications')
      .select('id')
      .eq('org_id', orgId)
      .eq('client_id', clientId);
    const ids = (appIds ?? []).map((a) => a.id);
    if (ids.length === 0) return [];
    const { data, error } = await supabase
      .from('ggj_b2b_email_threads')
      .select(`
        *,
        application:ggj_b2b_applications(
          id, status,
          candidate:ggj_b2b_candidates(id, full_name),
          job:ggj_b2b_jobs(id, title),
          client:ggj_b2b_clients(id, name),
          assigned_user:ggj_b2b_org_users!assigned_to(id, display_name, email)
        )
      `)
      .eq('org_id', orgId)
      .in('application_id', ids)
      .order('sent_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as EmailThread[];
  },

  async getById(id: string): Promise<EmailThread | null> {
    const { data } = await supabase
      .from('ggj_b2b_email_threads')
      .select(`
        *,
        application:ggj_b2b_applications(
          id, status,
          candidate:ggj_b2b_candidates(id, full_name),
          job:ggj_b2b_jobs(id, title),
          client:ggj_b2b_clients(id, name)
        )
      `)
      .eq('id', id)
      .single();
    return data as EmailThread | null;
  },

  /** Create a draft thread row — token generation happens server-side via DB defaults */
  async createDraft(payload: {
    org_id: string;
    application_id: string;
    sent_by?: string | null;
    recipient_email: string;
    recipient_name?: string;
    subject: string;
    body_html: string;
    template_key: string;
    next_followup_at?: string | null;
    in_reply_to_thread_id?: string | null;
  }): Promise<EmailThread> {
    const { data, error } = await supabase
      .from('ggj_b2b_email_threads')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data as EmailThread;
  },

  async getUpcomingFollowups(orgId: string): Promise<Array<{ id: string; application_id: string; next_followup_at: string; recipient_name: string | null; subject: string }>> {
    const { data, error } = await supabase
      .from('ggj_b2b_email_threads')
      .select('id, application_id, next_followup_at, recipient_name, subject')
      .eq('org_id', orgId)
      .not('next_followup_at', 'is', null)
      .eq('followup_stopped', false)
      .is('cta_response', null)
      .lt('followup_count', 3)
      .order('next_followup_at', { ascending: true })
      .limit(50);
    if (error) throw error;
    return (data ?? []).filter((r) => r.next_followup_at) as Array<{
      id: string;
      application_id: string;
      next_followup_at: string;
      recipient_name: string | null;
      subject: string;
    }>;
  },

  async stopFollowups(threadId: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_email_threads')
      .update({ followup_stopped: true, next_followup_at: null })
      .eq('id', threadId);
    if (error) throw error;
  },

  /**
   * C-28 — Queue a bulk email send by enqueuing one outbox event per
   * candidate. The existing outbox worker (see migrations 017, 029, 055)
   * picks up `status = 'pending'` rows and delivers the actual email; this
   * method does NOT send SMTP traffic itself.
   *
   * `orgId` is required so the insert passes the org-scoped RLS INSERT
   * policy added in migration 102 — the ticket's documented signature was
   * `{ candidateIds, templateId, variables }`, but Supabase needs the
   * tenant up-front and every caller already has it via `useAuth`.
   */
  async sendBulk(params: {
    orgId: string;
    candidateIds: string[];
    templateId: string;
    variables: Record<string, string>;
  }): Promise<{ queued: number }> {
    const { orgId, candidateIds, templateId, variables } = params;
    if (!orgId) throw new Error('orgId is required.');

    const uniqueIds = Array.from(new Set(candidateIds.filter(Boolean)));
    if (uniqueIds.length === 0) return { queued: 0 };

    const rows = uniqueIds.map((candidateId) => ({
      org_id: orgId,
      event_type: 'email.bulk_queued',
      aggregate_type: 'candidate',
      aggregate_id: candidateId,
      payload: {
        candidateId,
        templateId,
        variables,
      },
      status: 'pending',
    }));

    const { error, count } = await supabase
      .from('ggj_b2b_outbox_events')
      .insert(rows, { count: 'exact' });
    if (error) throw new Error(error.message);

    return { queued: count ?? rows.length };
  },
};
